import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

/**
 * A pairing payload, drawn as something a phone can actually read. Phase 67.
 *
 * WhatsApp Web hands over a **string**, not a picture — `2@abc…`, a few hundred characters — and
 * the screen was printing it verbatim in a `<code>` block. Nobody can scan text. This is the
 * missing step between what the engine returns and what somebody points their camera at.
 *
 * Rendered as an SVG rather than a canvas: it stays sharp on any screen, it survives a browser zoom,
 * and the phone camera reading it is doing so from several centimetres away where a blurry raster
 * is the difference between pairing and not.
 *
 * Drawn entirely in the browser. The payload is a credential — anybody who scans it attaches a
 * device to that WhatsApp account — so it never goes to an image service to be turned into a
 * picture, which is what the convenient QR APIs on the internet actually do with it.
 */
export function QrCode({
  payload,
  size = 240,
}: {
  payload: string;
  size?: number;
}): React.JSX.Element {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    QRCode.toString(payload, {
      type: 'svg',
      // `M` corrects around 15% damage, which is what a phone held at an angle needs, without
      // making the modules so dense that a small screen cannot resolve them.
      errorCorrectionLevel: 'M',
      margin: 2,
      width: size,
    })
      .then((drawn) => {
        if (!cancelled) {
          setSvg(drawn);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [payload, size]);

  if (failed || !svg) {
    // No placeholder square: an empty frame that looks like a QR code is worse than an empty frame.
    return (
      <div className="qr qr--empty" style={{ width: size, height: size }} aria-hidden="true" />
    );
  }

  return (
    <div
      className="qr"
      style={{ width: size, height: size }}
      role="img"
      aria-label="QR Code"
      /* The SVG is generated here from a string this component received; nothing user-authored
         reaches it. */
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
